/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// useCatalog — hook pour pré-charger et exposer le catalogue résolu du workspace
// Charge /api/catalog/resolved au mount, cache en mémoire pour éviter les ré-appels.
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { catalogAPI } from '../services/api';

let _cache = null;
let _cachePromise = null;

function loadCatalog() {
  if (_cache) return Promise.resolve(_cache);
  if (_cachePromise) return _cachePromise;
  _cachePromise = catalogAPI.resolved()
    .then(r => {
      _cache = r.data || { status_labels: [], formality_types: [], greffes: [], cabinets: [] };
      _cachePromise = null;
      return _cache;
    })
    .catch(err => {
      _cachePromise = null;
      throw err;
    });
  return _cachePromise;
}

export function invalidateCatalogCache() {
  _cache = null;
  _cachePromise = null;
}

export function useCatalog() {
  const [catalog, setCatalog] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    invalidateCatalogCache();
    setLoading(true);
    setError(null);
    loadCatalog()
      .then(c => { setCatalog(c); setLoading(false); })
      .catch(e => { setError(e); setLoading(false); });
  }, []);

  useEffect(() => {
    if (_cache) {
      setCatalog(_cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadCatalog()
      .then(c => { if (!cancelled) { setCatalog(c); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Helpers pour retrouver une entrée par id
  const findStatus       = useCallback((id) => catalog?.status_labels?.find(s => s.id === id) || null, [catalog]);
  const findFormalityType = useCallback((id) => catalog?.formality_types?.find(t => t.id === id) || null, [catalog]);
  const findSubType      = useCallback((id) => {
    if (!catalog) return null;
    for (const t of catalog.formality_types || []) {
      const sub = (t.sub_types || []).find(st => st.id === id);
      if (sub) return sub;
    }
    return null;
  }, [catalog]);
  const findGreffe       = useCallback((id) => catalog?.greffes?.find(g => g.id === id) || null, [catalog]);
  const findCabinet      = useCallback((id) => catalog?.cabinets?.find(c => c.id === id) || null, [catalog]);

  return {
    catalog,
    loading,
    error,
    refresh,
    statusLabels:   catalog?.status_labels   || [],
    formalityTypes: catalog?.formality_types || [],
    greffes:        catalog?.greffes         || [],
    cabinets:       catalog?.cabinets        || [],
    findStatus, findFormalityType, findSubType, findGreffe, findCabinet,
  };
}
